import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { getFreeDelivery, setDistanceCharges } from '../../../apollo'
import { Box, Typography, Input, Button, Alert, Switch } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const GET_FREE_DELIVERY = gql`
  ${getFreeDelivery}
`
const SET_DISTANCE_CHARGES = gql`
  ${setDistanceCharges}
`

// The far end of the distance scale: the per-km rate on the Store Charges card
// climbs until this distance, past which delivery is charged at one flat fee.
function DistanceCharges(props) {
  const { t } = props
  const restaurantId =
    props.restaurantId || localStorage.getItem('restaurantId') || ''

  const [farDistance, setFarDistance] = useState('')
  const [farDistanceFee, setFarDistanceFee] = useState('')
  // Off keeps the distance and fee saved but stops charging them
  const [farDistanceEnabled, setFarDistanceEnabled] = useState(true)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  const { data, refetch } = useQuery(GET_FREE_DELIVERY, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })
  const [mutate, { loading }] = useMutation(SET_DISTANCE_CHARGES)

  useEffect(() => {
    const config = data?.getFreeDelivery
    if (!config) return
    setFarDistance(String(config.farDistance ?? ''))
    setFarDistanceFee(String(config.farDistanceFee ?? ''))
    setFarDistanceEnabled(config.farDistanceEnabled !== false)
  }, [data])

  useEffect(() => {
    if (!successMessage) return
    const timeoutId = setTimeout(() => setSuccessMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const timeoutId = setTimeout(() => setErrorMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [errorMessage])

  // Keeps a blank box from being saved as a negative number
  const onNumberChange = setter => event => {
    const value = event.target.value
    if (value === '') {
      setter('')
      return
    }
    setter(String(Math.max(0, Number(value))))
  }

  const onSave = event => {
    event.preventDefault()
    const distance = Number(farDistance || 0)
    const fee = Number(farDistanceFee || 0)
    // A threshold with no fee behind it would quietly make long deliveries
    // free, so refuse the half-filled form rather than saving it
    if (farDistanceEnabled && distance > 0 && fee <= 0) {
      setErrorMessage(t('FixedAfterDistanceFeeRequired'))
      return
    }
    mutate({
      variables: {
        restaurantId,
        farDistance: distance,
        farDistanceFee: fee,
        farDistanceEnabled
      },
      onCompleted: () => {
        refetch()
        setSuccessMessage(t('Configuration saved successfully!'))
      },
      onError: error => {
        setErrorMessage(
          error?.message || t('An error occurred while saving configuration.')
        )
        console.error('Mutation error:', error)
      }
    })
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('DistanceCharges')}
          </Typography>
        </Box>
        <Box ml={10} mt={1}>
          <label>{farDistanceEnabled ? t('Disable') : t('Enable')}</label>
          <Switch
            checked={farDistanceEnabled}
            onChange={e => setFarDistanceEnabled(e.target.checked)}
            style={{ color: 'black' }}
          />
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>
              {t('FixedAfterDistance')}
            </Typography>
            <Typography variant="caption" className={classes.helperText}>
              {t('FixedAfterDistanceHelp')} {t('FreeDeliveryOverridesDistance')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-far-distance"
              name="input-far-distance"
              placeholder={t('FixedAfterDistancePlaceholder')}
              type="number"
              min={0}
              value={farDistance}
              onChange={onNumberChange(setFarDistance)}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('FixedAfterDistanceFee')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-far-distance-fee"
              name="input-far-distance-fee"
              placeholder={t('FixedAfterDistanceFee')}
              type="number"
              min={0}
              value={farDistanceFee}
              onChange={onNumberChange(setFarDistanceFee)}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading || !restaurantId}
              onClick={onSave}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(DistanceCharges)
